import React, { forwardRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import type { TabOrientation } from '../nativeTabsTypes.ts';

import { getTabColorAppearance, type TabColorKey } from './tabColorDefinitions.ts';
import { isStandardTabColorPaletteEnabled } from './tabColorFeatureGate.ts';

type TabColorMenuTriggerProps = Omit<
	React.ComponentPropsWithoutRef<typeof Pressable>,
	'aria-label' | 'children' | 'onMouseDown' | 'style' | 'xcss'
> & {
	currentColor?: TabColorKey;
	isDeletePreviewed?: boolean;
	label: string;
	orientation?: TabOrientation;
};

// The style prop assigns runtime values to these variables; cssMap consumes them below.
type TabColorMenuTriggerCustomProperties = React.CSSProperties & {
	'--tab-color-menu-trigger-background-color'?: string;
	'--tab-color-menu-trigger-border-color'?: string;
	'--tab-color-menu-trigger-text-color'?: string;
};

const styles = cssMap({
	trigger: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		alignSelf: 'stretch',
		paddingBlock: token('space.050'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.100'),
		borderRadius: 0,
		borderStartEndRadius: token('radius.full'),
		borderEndEndRadius: token('radius.full'),
		borderStyle: 'solid',
		borderWidth: token('border.width.focused'),
		borderInlineStartWidth: 0,
		transition: 'background .1s ease-out',
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.selected'),
		color: token('color.text.selected'),
	},
	// Matches the vertical pill's fixed corner radius so both ends stay consistent as titles wrap.
	triggerVertical: {
		borderStartEndRadius: token('radius.xxlarge'),
		borderEndEndRadius: token('radius.xxlarge'),
	},
	triggerDeletePreview: {
		color: token('color.text.danger'),
		backgroundColor: token('color.background.danger'),
		borderColor: token('color.border.danger'),
	},
	triggerFromCatalog: {
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		backgroundColor: 'var(--tab-color-menu-trigger-background-color)',
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		borderColor: 'var(--tab-color-menu-trigger-border-color)',
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		color: 'var(--tab-color-menu-trigger-text-color)',
	},
});

export const TabColorMenuTrigger: React.ForwardRefExoticComponent<
	TabColorMenuTriggerProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, TabColorMenuTriggerProps>(
	(
		{ currentColor, isDeletePreviewed = false, label, orientation = 'horizontal', ...triggerProps },
		ref,
	) => {
		const appearanceMode = isStandardTabColorPaletteEnabled() ? 'standard' : 'legacy';
		const activeTabPillColor = currentColor
			? getTabColorAppearance(currentColor, appearanceMode).tabPill.active
			: undefined;

		return (
			<Pressable
				{...triggerProps}
				ref={ref}
				aria-label={label}
				style={
					{
						'--tab-color-menu-trigger-background-color': activeTabPillColor?.backgroundColor,
						'--tab-color-menu-trigger-border-color': activeTabPillColor?.borderColor,
						'--tab-color-menu-trigger-text-color': activeTabPillColor?.textColor,
					} as TabColorMenuTriggerCustomProperties
				}
				xcss={cx(
					styles.trigger,
					orientation === 'vertical' && styles.triggerVertical,
					!isDeletePreviewed && activeTabPillColor && styles.triggerFromCatalog,
					isDeletePreviewed && styles.triggerDeletePreview,
				)}
				onMouseDown={(event) => event.preventDefault()}
			>
				<ShowMoreHorizontalIcon label="" color="currentColor" />
			</Pressable>
		);
	},
);

TabColorMenuTrigger.displayName = 'TabColorMenuTrigger';
